import React, { useState, useEffect, useCallback } from 'react';
import {
  Fab, Drawer, Box, Typography, IconButton, TextField, InputAdornment,
  List, ListItem, ListItemIcon, ListItemText, ListItemButton, Divider,
  Badge, Chip, CircularProgress, Alert
} from '@mui/material';
import {
  HelpOutline as HelpIcon, Close as CloseIcon, Search as SearchIcon,
  QuestionAnswer as FaqIcon, ConfirmationNumber as TicketIcon,
  Chat as ChatIcon, Circle as CircleIcon, ArrowForward as ArrowIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import supportService from '../../services/supportService';

const HelpWidget = () => {
  const { t } = useTranslation(['support', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [faqResults, setFaqResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [agentUnreadCount, setAgentUnreadCount] = useState(0);
  const [clientUnreadCount, setClientUnreadCount] = useState(0);
  const [isAgent, setIsAgent] = useState(false);

  const fetchAgentStatus = useCallback(async () => {
    try {
      const res = await supportService.getAgentStatus();
      if (res.success) setAgentStatus(res.data);
    } catch {
      // ignore
    }
  }, []);

  const checkUserIsAgent = useCallback(async () => {
    if (!user) {
      setIsAgent(false);
      return;
    }
    try {
      const res = await supportService.checkUserIsAgent();
      if (res.success) setIsAgent(res.data?.isAgent || false);
    } catch {
      setIsAgent(false);
    }
  }, [user]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await supportService.getUnreadCount();
      if (res.success) {
        const agentCount = res.data?.agentCount ?? 0;
        const clientCount = res.data?.clientCount ?? res.data?.count ?? 0;
        setAgentUnreadCount(agentCount);
        setClientUnreadCount(clientCount);
        setUnreadCount(agentCount + clientCount);
      }
    } catch {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    fetchAgentStatus();
    fetchUnreadCount();
    checkUserIsAgent();

    // Poll unread count every 30 seconds to keep badge fresh
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30_000);

    return () => clearInterval(interval);
  }, [fetchAgentStatus, fetchUnreadCount, checkUserIsAgent]);

  // Allow external components (e.g. legal pages) to open the help panel
  // via: document.dispatchEvent(new Event('krillou:open-help'))
  useEffect(() => {
    const handleOpenHelp = () => { setOpen(true); fetchUnreadCount(); };
    document.addEventListener('krillou:open-help', handleOpenHelp);
    return () => document.removeEventListener('krillou:open-help', handleOpenHelp);
  }, [fetchUnreadCount]);

  const handleSearch = useCallback(async (query) => {
    setSearchQuery(query);
    if (!query.trim() || query.length < 2) {
      setFaqResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await supportService.getFaq({ search: query, limit: 5 });
      if (res.success) {
        const articles = res.data?.categories?.flatMap(c => c.articles || [])
          || res.data?.articles || [];
        setFaqResults(articles.slice(0, 5));
      }
    } catch {
      setFaqResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      {/* Floating Help Button */}
      <Fab
        color="success"
        onClick={() => { setOpen(true); fetchUnreadCount(); }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
          boxShadow: 3
        }}
        aria-label={t('support:widget.title')}
      >
        <Badge badgeContent={unreadCount} color="error" overlap="circular">
          <HelpIcon />
        </Badge>
      </Fab>

      {/* Help Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, p: 0 } }}
      >
        {/* Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {t('support:widget.title')}
            </Typography>
            <IconButton onClick={() => setOpen(false)} sx={{ color: 'white' }} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {t('support:widget.subtitle')}
          </Typography>

          {/* Search FAQ */}
          <TextField
            fullWidth
            size="small"
            placeholder={t('support:faq.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.15)',
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputAdornment-root': { color: 'rgba(255,255,255,0.7)' },
              '& input::placeholder': { color: 'rgba(255,255,255,0.6)', opacity: 1 }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* FAQ search results */}
        {searchQuery.length >= 2 && (
          <Box sx={{ px: 2, py: 1 }}>
            {searching ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : faqResults.length > 0 ? (
              <List dense disablePadding>
                {faqResults.map((article) => (
                  <ListItemButton
                    key={article.id}
                    onClick={() => handleNavigate(`/support/faq/${article.slug || article.id}`)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FaqIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={article.title}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                    <ArrowIcon fontSize="small" color="action" />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Alert severity="info" variant="outlined" sx={{ my: 1 }}>
                {t('support:faq.noResults')}
              </Alert>
            )}
            <Divider sx={{ mt: 1 }} />
          </Box>
        )}

        {/* Quick Actions */}
        <Box sx={{ p: 2, flex: 1 }}>
          {/* Agent status */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <CircleIcon sx={{ fontSize: 10, color: agentStatus?.isAvailable ? 'success.main' : 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {agentStatus?.isAvailable
                ? t('support:widget.agentOnline')
                : t('support:widget.agentOffline')
              }
            </Typography>
          </Box>

          <List disablePadding>
            {/* FAQ */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigate('/support/faq')}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 1.5 }}
              >
                <ListItemIcon>
                  <FaqIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('support:faq.title')}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
                <ArrowIcon color="action" />
              </ListItemButton>
            </ListItem>

            {/* Create ticket */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => handleNavigate('/support/new')}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 1.5 }}
              >
                <ListItemIcon>
                  <TicketIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={t('support:widget.createTicket')}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
                <ArrowIcon color="action" />
              </ListItemButton>
            </ListItem>

            {/* My tickets (if logged in) */}
            {user && (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavigate('/support/tickets')}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 1.5 }}
                >
                  <ListItemIcon>
                    <Badge badgeContent={clientUnreadCount} color="error">
                      <ChatIcon color="primary" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={t('support:ticket.title')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <ArrowIcon color="action" />
                </ListItemButton>
              </ListItem>
            )}

            {/* Agent dashboard (only for agents, not admins) */}
            {user && isAgent && !user.isAdmin && (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleNavigate('/agent-dashboard')}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 1.5, bgcolor: 'action.hover' }}
                >
                  <ListItemIcon>
                    <Badge badgeContent={agentUnreadCount} color="error">
                      <SupportAgentIcon color="primary" />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={t('support:agent.dashboard')}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                  <ArrowIcon color="action" />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default HelpWidget;
